import {
  IRRIGATION_SCHEDULE,
  IRRIGATION_TYPE,
  IRRIGATION_UNITS,
  IrrigationInput,
  RDIrrigation,
  ResourceDetail,
} from '@cibo/core'
import { TextField as FMTextField, NumberInputField, SelectField } from '@cibo/ui'
import {
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material'
import FormLabel from '@mui/material/FormLabel'
import { Field, useField } from 'formik'
import { filter } from 'ramda'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailEditorPropsByYear } from '../types'
import { useIsRequired } from '../useIsRequired'
import { useShowDetailAttribute } from '../useShowDetailAttribute'
import { findInitialValuesDetail } from '../utils'

const clearEmptyStrings = filter(attribute => attribute !== '')

const SECONDARY_ATTRIBUTES = ['schedule', 'irrigationType', 'rate', 'units']
const includesSecondaryAttribute = (values: any) =>
  Object.keys(clearEmptyStrings(values)).some(key => SECONDARY_ATTRIBUTES.includes(key))

type IrrigationEditorProps = DetailEditorPropsByYear & { simple?: boolean }

const isNotIrrigated = (detail: ResourceDetail<IrrigationInput> | undefined) =>
  detail?.result === false
    ? true
    : detail?.result === true
    ? false
    : includesSecondaryAttribute(detail?.input || {})
    ? false
    : detail?.input
    ? true
    : undefined

const isSufficientIrrigation = (detail: ResourceDetail<IrrigationInput> | undefined) =>
  detail?.result === false
    ? 'no'
    : detail?.result === true
    ? 'yes'
    : includesSecondaryAttribute(detail?.input || {})
    ? 'yes'
    : detail?.input
    ? 'no'
    : ''

export const IrrigationEditor = ({
  fieldModels,
  name = 'irrigationEditor',
  year,
  requirement,
  filterInputs,
  simple = false,
}: IrrigationEditorProps) => {
  const { t } = useTranslation('@cibo/landmanager/IrrigationEditor')
  const isRequired = useIsRequired(requirement)
  const isResultsOnly = requirement?.resultsOnly
  const show = useShowDetailAttribute<IrrigationInput>(filterInputs)
  const detail = findInitialValuesDetail<ResourceDetail<IrrigationInput> | undefined>('irrigation')(
    {
      fieldModels,
      year,
    }
  )

  const fieldHelpers = useField<RDIrrigation['input'] | object>(name as any)
  const { setTouched, setValue } = fieldHelpers[2]

  const [sufficientIrrigation, setSufficientIrrigation] = useState(
    simple ? undefined : isSufficientIrrigation(detail)
  )
  // simple mode: true = not irrigated (fields disabled), false = irrigated (fields enabled)
  const [notIrrigated, setNotIrrigated] = useState(simple ? isNotIrrigated(detail) : undefined)

  const setInputValues = () =>
    setValue({
      irrigationType: isRequired('irrigationType') ? detail?.input?.irrigationType : undefined,
      schedule: isRequired('schedule') ? detail?.input?.schedule : '',
      rate: isRequired('rate') ? detail?.input?.rate : '',
      units: isRequired('units') ? detail?.input?.units : undefined,
      totalAcreInches: isRequired('totalAcreInches') ? detail?.input?.totalAcreInches : null,
    })

  const handleSufficientIrrigationChange = (event: ChangeEvent | null, value: string) => {
    setTouched(true, false)
    setSufficientIrrigation(value)
    if (value === 'yes') {
      setInputValues()
    } else {
      setValue({})
    }
  }

  const handleNotIrrigatedChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setTouched(true, false)
    setNotIrrigated(checked)
    if (!checked) {
      setValue({})
    } else {
      setValue({})
    }
  }

  useEffect(() => {
    if (simple) {
      setNotIrrigated(isNotIrrigated(detail))
    } else {
      setSufficientIrrigation(isSufficientIrrigation(detail))
    }
  }, [detail])

  const showFields = !isResultsOnly && (simple ? !notIrrigated : sufficientIrrigation === 'yes')

  const fieldsDisabled = isResultsOnly || notIrrigated || sufficientIrrigation === 'no'

  return (
    <FormControl>
      <Stack spacing={simple ? 1 : 2}>
        {simple ? (
          <FormControlLabel
            control={<Checkbox checked={notIrrigated} onChange={handleNotIrrigatedChange} />}
            label={t('thisFieldWasNotIrrigated')}
          />
        ) : (
          <FormControl>
            <FormLabel required>{t('sufficientIrrigation')}</FormLabel>

            <RadioGroup
              data-testid={`${name}.sufficientIrrigation`}
              value={sufficientIrrigation}
              onChange={handleSufficientIrrigationChange}
            >
              <FormControlLabel
                value={'yes'}
                control={<Radio />}
                label={`${t('yes')}`}
                data-testid={`${name}.sufficientIrrigation.yes`}
              />
              <FormControlLabel
                value={'no'}
                control={<Radio />}
                label={`${t('no')}`}
                data-testid={`${name}.sufficientIrrigation.no`}
              />
            </RadioGroup>
          </FormControl>
        )}
        <Collapse in={showFields}>
          <Stack spacing={simple ? 1 : 2}>
            <FormControl>
              <FormLabel disabled={fieldsDisabled} required={isRequired('schedule')}>
                {t('schedule')}
              </FormLabel>

              <SelectField
                disabled={fieldsDisabled}
                fieldName={`${name}.schedule`}
                options={IRRIGATION_SCHEDULE}
                renderOption={context => t('schedule', { context })}
              />
            </FormControl>

            <FormControl>
              <FormLabel disabled={fieldsDisabled} required={isRequired('irrigationType')}>
                {t('irrigationType')}
              </FormLabel>

              <SelectField
                disabled={fieldsDisabled}
                fieldName={`${name}.irrigationType`}
                options={IRRIGATION_TYPE}
                renderOption={context => t('type', { context })}
              />
            </FormControl>

            {show('totalAcreInches') && (
              <FormControl>
                <FormLabel disabled={fieldsDisabled} required={isRequired('totalAcreInches')}>
                  {t('acreInchesApplied')}
                </FormLabel>
                <Field
                  disabled={fieldsDisabled}
                  component={FMTextField}
                  name={`${name}.totalAcreInches`}
                  InputProps={{
                    type: 'number',
                    endAdornment: (
                      <InputAdornment position="end">{t('acre-inches')}</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                    },
                  }}
                  data-testid={`${name}.totalAcreInches`}
                />
              </FormControl>
            )}

            <FormControl>
              <FormLabel
                disabled={fieldsDisabled}
                required={isRequired('units') || isRequired('rate')}
              >
                {t('rateUnitLabel')}
              </FormLabel>

              <Grid container spacing={1}>
                <Grid
                  size={{ xs: 6 }}
                  sx={{
                    marginBottom: simple ? 2 : undefined,
                    '& .MuiFormControl-root': {
                      width: '100%',
                    },
                  }}
                >
                  <NumberInputField
                    disabled={fieldsDisabled}
                    name={`${name}.rate`}
                    data-testid={`${name}.rate`}
                    min={0}
                  />
                </Grid>

                <Grid
                  size={{ xs: 6 }}
                  sx={{
                    marginBottom: simple ? 2 : undefined,
                    '& .MuiInputBase-formControl': {
                      width: '100%',
                    },
                  }}
                >
                  <SelectField
                    disabled={fieldsDisabled}
                    fieldName={`${name}.units`}
                    options={IRRIGATION_UNITS}
                    renderOption={context => t('unit', { context })}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Stack>
        </Collapse>
      </Stack>
    </FormControl>
  )
}
